/**
 * ApprovalsTable.js
 *
 * Specialized table for displaying device approvals
 * Shows: Device ID, Device Name, Department, Status, Requested Date, Resubmissions, Actions
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import DataTable from './DataTable';
import ApprovalStatusBadge from './ApprovalStatusBadge';
import { DeviceIDBadge } from '../../../../shared/components/common/Badges';
import ModalCloseButton from '../../../../shared/components/modals/ModalCloseButton';
import '../../../approvals/components/ApprovalModal.css';

/**
 * ApprovalsTable Component
 *
 * Props:
 * - data: Array of approval objects
 * - onEditDevice: Handler for edit/resubmit button (rejected devices only)
 * - onViewDevice: Handler for view button (pending and approved devices)
 * - loading: Loading state
 * - className: Additional CSS classes
 * - departments: Array of department objects with icons
 * - user: Logged-in user (for "You" reviewer chip)
 */
const ApprovalsTable = ({
  data = [],
  onEditDevice = null,
  onViewDevice = null,
  loading = false,
  className = '',
  departments = [],
  user = null,
}) => {
  const [selectedRejection, setSelectedRejection] = useState(null);

  useEffect(() => {
    if (!selectedRejection) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedRejection(null);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRejection]);

  const getDepartmentIcon = (departmentName) => {
    if (!departmentName || !departments || departments.length === 0) {
      return '🏢';
    }

    const dept = departments.find(
      (d) => d.name?.toLowerCase() === departmentName?.toLowerCase()
    );

    return dept?.icon || '🏢';
  };

  const getReviewerDisplayName = (approval) => {
    if (!approval?.reviewed_by_employee_id) return null;
    if (user?.employee_id && approval.reviewed_by_employee_id === user.employee_id) {
      return 'You';
    }
    return approval.reviewed_by_name || approval.reviewed_by_employee_id || 'Unknown';
  };

  const columns = [
    {
      key: 'device_identifier',
      label: 'Device',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#1f2937' }}>
            {row.device_name || 'N/A'}
          </span>
          <DeviceIDBadge deviceId={value || 'N/A'} size="small" />
        </div>
      )
    },
    {
      key: 'device_data',
      label: 'Department',
      sortable: true,
      render: (deviceData, row) => {
        const dept = deviceData?.department || row?.department || row?.device_data?.department || 'N/A';
        const deptIcon = getDepartmentIcon(dept);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="department-icon-small">{deptIcon}</span>
            <span className="department-text">{dept}</span>
          </div>
        );
      }
    },
    {
      key: 'approval_status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <ApprovalStatusBadge status={value} size="small" />
      )
    },
    {
      key: 'requested_at',
      label: 'Requested Date',
      sortable: true,
      render: (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return date.toLocaleDateString();
      }
    },
    {
      key: 'resubmission_count',
      label: 'Resubmissions',
      sortable: true,
      render: (value) => (
        value > 0 ? (
          <span className="approval-resubmission-badge">{value}</span>
        ) : (
          '0'
        )
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, row) => {
        const deviceId = row.device_id || row.id || row.approval_id;

        return (
          <div className="action-buttons">
            {row.approval_status === 'in_draft' && onEditDevice && (
              <button
                className="btn-action btn-edit"
                onClick={(e) => {
                  e.stopPropagation();

                  if (deviceId) {
                    onEditDevice(deviceId);
                  } else {
                    console.error('❌ Device ID not found! Available fields:', Object.keys(row), 'Row data:', row);
                    alert('Error: Device ID not found. Please check the browser console for details.');
                  }
                }}
                title="Continue editing draft device"
              >
                Continue editing
              </button>
            )}
            {row.approval_status === 'rejected' && onEditDevice && (
              <button
                className="btn-action btn-edit"
                onClick={(e) => {
                  e.stopPropagation();

                  if (deviceId) {
                    onEditDevice(deviceId);
                  } else {
                    console.error('❌ Device ID not found! Available fields:', Object.keys(row), 'Row data:', row);
                    alert('Error: Device ID not found. Please check the browser console for details.');
                  }
                }}
                title="Edit and resubmit device"
              >
                Edit & Resubmit
              </button>
            )}
            {row.approval_status === 'rejected' && (
              <button
                type="button"
                className="btn-action btn-view-reason"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRejection(row);
                }}
                title="View rejection reason"
              >
                View reason
              </button>
            )}
            {row.approval_status === 'pending' && onViewDevice && (
              <button
                className="btn-action btn-view"
                onClick={(e) => {
                  e.stopPropagation();

                  if (deviceId) {
                    onViewDevice(deviceId);
                  } else {
                    console.error('❌ Device ID not found! Available fields:', Object.keys(row), 'Row data:', row);
                    alert('Error: Device ID not found. Please check the browser console for details.');
                  }
                }}
                title="View device details (read-only)"
              >
                View
              </button>
            )}
            {row.approval_status === 'approved' && onViewDevice && (
              <button
                className="btn-action btn-view"
                onClick={(e) => {
                  e.stopPropagation();

                  if (deviceId) {
                    onViewDevice(deviceId);
                  } else {
                    console.error('❌ Device ID not found! Available fields:', Object.keys(row), 'Row data:', row);
                    alert('Error: Device ID not found. Please check the browser console for details.');
                  }
                }}
                title="View device details"
              >
                View
              </button>
            )}
            {row.approval_status === 'pending' && !onViewDevice && (
              <span style={{ fontSize: '0.85rem', color: '#999' }}>
                Awaiting Review
              </span>
            )}
          </div>
        );
      }
    }
  ];

  const reviewerDisplayName = selectedRejection
    ? getReviewerDisplayName(selectedRejection)
    : null;

  const rejectionReasonDialog = selectedRejection && (
    <div
      className="approval-modal-overlay"
      onClick={() => setSelectedRejection(null)}
      role="presentation"
    >
      <div
        className="approval-modal-content approval-modal-content--compact"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rejection-reason-title"
      >
        <div className="approval-modal-header">
          <h2 id="rejection-reason-title">Rejection Reason</h2>
          <ModalCloseButton onClick={() => setSelectedRejection(null)} title="Close (Esc)" />
        </div>

        <div className="approval-modal-body">
          <div className="approval-device-details">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>
                {selectedRejection.device_name || 'Device'}
              </span>
              <DeviceIDBadge
                deviceId={selectedRejection.device_identifier || 'N/A'}
                size="small"
              />
            </div>

            <div className="approval-details-grid">
              <div className="approval-detail-row">
                <label>Status:</label>
                <span>
                  <ApprovalStatusBadge status="rejected" size="small" />
                </span>
              </div>
              {reviewerDisplayName && (
                <div className="approval-detail-row">
                  <label>Rejected By:</label>
                  <span>
                    {reviewerDisplayName === 'You' ? (
                      <span className="doc-uploader-you" title="Reviewed by you">
                        You
                      </span>
                    ) : (
                      reviewerDisplayName
                    )}
                  </span>
                </div>
              )}
              {selectedRejection.reviewed_at && (
                <div className="approval-detail-row">
                  <label>Reviewed At:</label>
                  <span>{new Date(selectedRejection.reviewed_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          <div className="approval-device-details">
            <h3>Rejection Reason</h3>
            <p className="approval-notes-text">
              {selectedRejection.decision_reason?.trim() || 'No reason provided'}
            </p>
          </div>
        </div>

        <div className="approval-modal-footer">
          <button
            type="button"
            className="action-btn action-secondary"
            onClick={() => setSelectedRejection(null)}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        disablePagination={true}
        className={`approvals-table ${className}`}
      />
      {ReactDOM.createPortal(
        rejectionReasonDialog,
        document.getElementById('modal-root') || document.body
      )}
    </>
  );
};

export default ApprovalsTable;
